import React, { useRef } from 'react';
import Header from './Header';
import HeroSection from './HeroSection';
import FeatureIntro from './FeatureIntro';
import SlideCards from './SlideCards';
import Testimonials from './Testimonials';
import FAQSection from './FAQSection';
import ContactForm from './ContactForm';
import EnhancedFooter from './EnhancedFooter';
import ErrorBoundary from '../../../shared/components/utility/ErrorBoundary';
import SEOHead from './SEOHead';
import FloatingContactButton from '../../../shared/components/ui/overlays/FloatingContactButton';
import WorkflowSection from './WorkflowSection';
import MenuBar from './MenuBar';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useFloatingButton } from '../hooks/useFloatingButton';
import '../styles/LandingPage.css';

const LandingPage: React.FC = () => {
  const contactFormRef = useRef<HTMLDivElement>(null);
  
  // セクション用のref
  const heroSectionRef = useRef<HTMLDivElement>(null);
  const featureIntroRef = useRef<HTMLDivElement>(null);
  const slideCardsRef = useRef<HTMLDivElement>(null);
  const faqSectionRef = useRef<HTMLDivElement>(null);
  
  // 各セクション用のアニメーションフック
  const heroAnimation = useScrollAnimation({ threshold: 0.2, delay: 200 });
  const featureAnimation = useScrollAnimation({ threshold: 0.1, delay: 100 });
  const slideCardsAnimation = useScrollAnimation({ threshold: 0.1, delay: 150 });
  const testimonialsAnimation = useScrollAnimation({ threshold: 0.1, delay: 150 });
  const fullscreenImageAnimation = useScrollAnimation({ threshold: 0.1, delay: 100 });
  const contactAnimation = useScrollAnimation({ threshold: 0.1, delay: 200 });
  
  // フローティングボタン制御フック
  const floatingButton = useFloatingButton({
    showStartRef: heroSectionRef,
    hideEndRef: contactFormRef,
    offset: 50
  });

  const scrollToContact = () => {
    const el = contactFormRef.current;

    if (!el) {
      console.warn('❌ ref is null at click time');
      return;
    }

    const rect = el.getBoundingClientRect();
    console.log('📍 Element position (before scroll):', rect.top);

    const offsetTop = window.scrollY + rect.top;

    window.scrollTo({
      top: offsetTop - 100, // 必要に応じてオフセット調整
      behavior: 'smooth',
    });

    setTimeout(() => {
      const afterRect = el.getBoundingClientRect();
      console.log('✅ Element position (after scroll):', afterRect.top);
    }, 1000);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const scrollToFeatures = () => {
    const el = featureIntroRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const offsetTop = window.scrollY + rect.top;

    window.scrollTo({
      top: offsetTop - 100, // ヘッダー分のオフセット
      behavior: 'smooth',
    });
  };

  const scrollToUsage = () => {
    const el = slideCardsRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const offsetTop = window.scrollY + rect.top;

    window.scrollTo({
      top: offsetTop - 100, // ヘッダー分のオフセット
      behavior: 'smooth',
    });
  };

  return (
    <ErrorBoundary>
      <SEOHead />
      
      {/* MenuBar */}
      <MenuBar 
        onHomeClick={scrollToTop}
        onFeaturesClick={scrollToFeatures}
        onUsageClick={scrollToUsage}
        onContactClick={scrollToContact}
      />
      
      <div className="landing-container">
        <Header />
        <div 
          ref={heroSectionRef}
          className={`hero-animate ${heroAnimation.isVisible ? 'visible' : ''}`}
        >
          <div ref={heroAnimation.ref as React.RefObject<HTMLDivElement>}>
            <HeroSection onContactClick={scrollToContact} />
          </div>
        </div>
        <div 
          ref={featureIntroRef}
          className={`section-animate ${featureAnimation.isVisible ? 'visible' : ''}`}
        >
          <div ref={featureAnimation.ref as React.RefObject<HTMLDivElement>}>
            <FeatureIntro />
          </div>
        </div>
        <div 
          ref={slideCardsRef}
          className={`section-animate delay-100 ${slideCardsAnimation.isVisible ? 'visible' : ''}`}
        >
          <div ref={slideCardsAnimation.ref as React.RefObject<HTMLDivElement>}>
            <SlideCards />
          </div>
        </div>
        <div 
          ref={testimonialsAnimation.ref as React.RefObject<HTMLDivElement>}
          className={`section-animate delay-150 ${testimonialsAnimation.isVisible ? 'visible' : ''}`}
        >
          <Testimonials />
        </div>
        <WorkflowSection />
        <div ref={faqSectionRef}>
          <FAQSection />
        </div>
        <div 
          ref={contactFormRef}
          className={`contact-animate ${contactAnimation.isVisible ? 'visible' : ''}`}
        >
          <div ref={contactAnimation.ref as React.RefObject<HTMLDivElement>}>
            <ContactForm />
          </div>
        </div>
        <EnhancedFooter />
        {/* <AccessibilityEnhancer /> */}
        
        {/* フローティングお問い合わせボタン */}
        <FloatingContactButton 
          isVisible={floatingButton.isVisible}
          onClick={scrollToContact}
        />
      </div>
    </ErrorBoundary>
  );
};

export default LandingPage;